import React, {createContext, useEffect, useReducer} from 'react';
import {GraphQlSchema, GraphQlType} from '../../../types/GraphQlSchema';
import ObjectsService, {
  ObjectDetailsResponse,
  ObjectInfoResponse
} from '../../../services/objects-service';
import {ObjectContextActionType, ObjectContextReducer} from './ObjectContextReducer';
import {ObjectState} from '../ObjectState';
import {Maybe} from '../../../types/Maybe';

export const initialState: ObjectState = {
  details: undefined,
  loading: false,
  errors: []
};

export const ObjectContext = createContext(initialState);

export type ObjectContextProviderProps = {
  graphQlSchema: GraphQlSchema;
  objectId: string;
  objectType?: string;
  children?: any;
  forceNewTabs?: boolean
};

export const ObjectContextProvider = (props: ObjectContextProviderProps) => {
  const [state, dispatch] = useReducer(ObjectContextReducer, initialState);

  useEffect(() => {

    const setLoading = () => {
      dispatch({
        type: ObjectContextActionType.LOADING,
        payload: {}
      });
    };

    const setDetails = (detailsResponse: ObjectDetailsResponse) => {
      dispatch({
        type: ObjectContextActionType.GET_OBJECT,
        payload: {
          details: detailsResponse.details,
          errors: detailsResponse.errors
        }
      });
    };

    const setErrors = (errors: Array<any>) => {
      dispatch({
        type: ObjectContextActionType.ERROR,
        payload: {
          errors: errors || []
        }
      });
      return Promise.reject(errors);
    };

    const loadObjectType = () => {
      return ObjectsService.loadInfo(props.objectId)
        .then((typeResponse: ObjectInfoResponse) => typeResponse.type.type)
        .catch((errorResponse: ObjectInfoResponse) => setErrors(errorResponse.errors));
    };

    const loadObjectDetails = (objectType: string) => {
      const graphQlSchema: GraphQlSchema = props.graphQlSchema;
      const graphQlType: Maybe<GraphQlType> = graphQlSchema.objectTypes.get(objectType);
      if (!graphQlType) {
        return setErrors([`Object type ${objectType} is not present in the GraphQL schema`]);
      }

      return ObjectsService.loadDetails(props.objectId, graphQlType, graphQlSchema)
        .then((detailsResponse: ObjectDetailsResponse) => setDetails(detailsResponse))
        .catch((errorsResponse: ObjectDetailsResponse) => setErrors(errorsResponse.errors));
    };

    setLoading();

    if (!props.objectType) {
      loadObjectType().then((objectType: string) => loadObjectDetails(objectType));
    } else {
      loadObjectDetails(props.objectType);
    }

    // Load the details on init & on changes
  }, [props.objectId, props.objectType, props.graphQlSchema]);

  return (
    <ObjectContext.Provider
      value={{
        details: state.details,
        loading: state.loading,
        errors: state.errors,
        forceNewTabs: props.forceNewTabs
      }}>
      {props.children}
    </ObjectContext.Provider>
  );
};
